/**
 * Base app error.
 * Use for extending.
 */

import {md5} from 'js-md5';

export class AppError extends Error {
  constructor(message = '') {
    super(message);
    this.name = 'AppError';
  }

  static jsonReplacer(key, value) {
    if (key === 'stack') {
      return value.split('\n');
    }

    return value;
  }

  stringify({ignoreStack = false} = {}): string {
    const obj: this & {
      message: string;
      stack?: string;
    } = {...this, message: this.message};

    if (!ignoreStack) {
      obj.stack = this.stack;
    }

    return JSON.stringify(obj, AppError.jsonReplacer, 2);
  }

  toLoggerError({ignoreStack = false} = {}) {
    const message = this.stringify({ignoreStack});
    const hash = md5(message);
    const name = `${this.name} - ${hash}`;

    return {message, name};
  }
}

export class NetworkError extends AppError {
  code: string;

  constructor(code: string, message: string) {
    super(message);
    this.name = 'NetworkError';
    this.code = code;
  }
}

export class CancelRequestError extends NetworkError {
  constructor() {
    super('api_request.is_canceled', 'The request has been canceled');
    this.name = 'CancelRequestError';
  }

  static isAbortSignalError(error) {
    /**
     * @see https://developer.mozilla.org/en-US/docs/Web/API/DOMException
     */
    return error.name === 'AbortError' || error.code === 20;
  }
}

export class HttpRequestError extends NetworkError {
  statusCode: number;

  constructor(statusCode: number, message = 'Request failed') {
    super(`error.http.${statusCode}`, message);
    this.name = 'HttpRequestError';
    this.statusCode = statusCode;
  }

  static isAmazonCodeDeploy(error) {
    return error instanceof HttpRequestError && error.statusCode === 503;
  }

  static isBanByIp(error) {
    return error instanceof HttpRequestError && error.statusCode === 429;
  }
}

export class NoConnectionError extends NetworkError {
  constructor() {
    super('error.no_connection', 'No network connection');
    this.name = 'NoConnectionError';
  }

  logDev(endpoint: string, methods: string) {
    if (process.env.NODE_ENV === 'development') {
      // eslint-disable-next-line no-console
      console.warn(
        `Request to ${endpoint} (${methods}) had been failed ${this.message}`,
      );
    }
  }
}

/**
 * Throw when target object doesn't have required property
 * for latest code.
 */
export class RequiredPropertyError extends AppError {
  constructor(propName: string) {
    super(`No required data: ${propName}`);

    this.name = 'RequiredPropertyError';
  }
}

/**
 * Use as general error in any store methods
 */
export class StoreError extends AppError {
  reason: Error;

  constructor(reason: Error) {
    super(reason.message);
    this.name = 'StoreError';
    this.reason = reason;
  }
}

export class WebsocketError extends NetworkError {
  statusCode: number; // make WS statuses?

  constructor(statusCode: number, message = 'Websocket connection error') {
    super(`error.websocket.${statusCode}`, message);
    this.name = 'WebsocketError';
    this.statusCode = statusCode;
  }
}
